import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";

// mui
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";

import InputAdornment from "@mui/material/InputAdornment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ButtonBase from "@mui/material/ButtonBase";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// lib
import formatMoney from "lib/formatMoney";

import sleep from "lib/sleep";
// components
import SpreadLTVTable from "components/Calculators/DSCRCalculator/components/SpreadLTVTable";
import CalculatorQuestions from "components/Calculators/DTICalculator/components/CalculatorQuestions";
import getDSCRLTVTiers from "components/Loans/utils/getDSCRLTVTiers";

import loadCalculation from "components/Calculators/components/utils/loadCalculation";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);
import SaveSharePDF from "components/Calculators/components/SaveSharePDF";

import QuestionRenderer from "components/Calculators/components/QuestionRenderer";
import Authenticator from "components/Authenticator";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

const calcType = "DTI";

export default function DTICalculator({
  externalCalc,
  loadCalcs,
  refCalcs,
  externalExampleCalc,
}) {
  const theme = useTheme();
  const router = useRouter();

  const [suggestionChoice, setSuggestionChoice] = useState(false);

  const {
    user,
    userProfile,
    isNewUser,
    setSnackbarData,
    showLogInDialog,
    setShowLogInDialog,
  } = useStore(AppStore);
  const [loanQuoteData, setLoanQuoteData] = useState({});
  const [resultsData, setResultsData] = useState([]);
  const [singleItemAnalysisData, setSingleItemAnalysisData] = useState({});

  const [showQuestionDescription, setShowQuestionDescription] = useState({
    "Monthly income": true,
    "Monthly expenses": true,
    "Total investment": true,
    "Internal rate of return": false, // Start collapsed
  });
  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({});

  // Add state for tracking value changes
  const [valueChanges, setValueChanges] = useState({});
  // Add original value ref
  const originalValueRef = useRef({});
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);
  // DSCR
  const [creditScore, setCreditScore] = useState(null);

  const [anchorEl, setAnchorEl] = useState({});

  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = ["Salary", "Investment income", "Other income"];
  const secondPartOfQuestions = [
    "Mortgage",
    "Rent",
    "Property tax",
    "Insurance",
    "HOA",
    "Credit card payment",
    "Student loan payment",
    "Auto loan payment",
    "Other liability payments",
  ];
  const thirdPart = ["Calculation name", "Address (optional)"];

  // Add this state at the top with other state declarations
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  const [isInView, setIsInView] = useState(false);
  // Add ref to track if expansion has been triggered
  const hasExpandedRef = useRef(false);

  // Add state for DTI calculation
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [dtiRatio, setDTIRatio] = useState(0);

  // Update the scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (!hasExpandedRef.current) {
        const irrSection = document.getElementById("pdf-4-irr-results");
        if (irrSection) {
          const rect = irrSection.getBoundingClientRect();
          const isLiftedOffBottom = rect.bottom < window.innerHeight;

          if (isLiftedOffBottom) {
            setShowQuestionDescription((prev) => ({
              ...prev,
              "Internal rate of return": true,
            }));
            hasExpandedRef.current = true; // Mark as expanded
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };

  // Custom hook to get the previous state
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  // Save the previous state
  const previousValues = usePrevious(currentValues);

  const defaultValues = {
    // "Amortization type": "Principal and interest",
    // "Payment frequency": "Monthly",
  };

  useEffect(() => {
    loadCalculation(
      defaultValues,
      externalCalc,
      externalExampleCalc,
      CalculatorQuestions,
      setQuestions,
      setAnswers,
      setCurrentValues,
      setShowUpdateFields
    );
  }, []);

  useEffect(() => {
    if (Object.keys(currentValues).length > 0) {
      // Calculate total monthly income
      const salary = parseFloat(currentValues["Salary"]) || 0;
      const investmentIncome =
        parseFloat(currentValues["Investment income"]) || 0;
      const otherIncome = parseFloat(currentValues["Other income"]) || 0;
      const totalMonthlyIncome = salary + investmentIncome + otherIncome;
      setMonthlyIncome(totalMonthlyIncome);

      // Calculate total monthly expenses
      const mortgage = parseFloat(currentValues["Mortgage"]) || 0;
      const rent = parseFloat(currentValues["Rent"]) || 0;
      const propertyTax = parseFloat(currentValues["Property tax"]) || 0;
      const insurance = parseFloat(currentValues["Insurance"]) || 0;
      const hoa = parseFloat(currentValues["HOA"]) || 0;
      const creditCard = parseFloat(currentValues["Credit card payment"]) || 0;
      const studentLoan =
        parseFloat(currentValues["Student loan payment"]) || 0;
      const autoLoan = parseFloat(currentValues["Auto loan payment"]) || 0;
      const otherLiabilities =
        parseFloat(currentValues["Other liability payments"]) || 0;

      const totalMonthlyExpenses =
        mortgage +
        rent +
        propertyTax +
        insurance +
        hoa +
        creditCard +
        studentLoan +
        autoLoan +
        otherLiabilities;
      setMonthlyExpenses(totalMonthlyExpenses);

      // Calculate DTI ratio
      const ratio =
        totalMonthlyExpenses > 0
          ? (totalMonthlyExpenses / totalMonthlyIncome) * 100
          : 0;
      setDTIRatio(ratio);
    }
  }, [currentValues]);

  const handleUserSelection = (
    value,
    answer,
    answers,
    questions,
    setAnswers
  ) => {
    const questionsSlice = questions.slice();
    const updatedAnswers = answers.slice(0);
    const isValidAnswer = answer.q_validation_func(value, answers);

    if (isValidAnswer) {
      const targetAnswer = updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0];

      // Calculate value change for currency fields
      if (answer.a_type === "currency") {
        // Store original value when user starts typing
        if (!originalValueRef.current[answer.q_title]) {
          originalValueRef.current[answer.q_title] =
            parseFloat(currentValues[answer.q_title]) || 0;
        }

        const newValue = parseFloat(value) || 0;
        const difference = newValue - originalValueRef.current[answer.q_title];

        if (difference !== 0) {
          setValueChanges((prev) => ({
            ...prev,
            [answer.q_title]: difference,
          }));

          // Clear the change and original value after 2 seconds
          setTimeout(() => {
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges[answer.q_title];
              return newChanges;
            });
            delete originalValueRef.current[answer.q_title];
          }, 2000);
        }
      }

      // save users answer
      targetAnswer.a_string = value;
      // reset error just in case
      targetAnswer.a_error = false;

      // determine if need to insert follow up questions
      // const next_q_id = answer.q_next_step_func(targetAnswer, answers);
      const next_q_id = answer.q_next_step_func(answer, answers, questions);

      const q_ids = updatedAnswers.map((answer) => {
        return answer.q_id;
      });

      // NEXT QUESTION is always next in position
      // NOT like before when it was based on length of q_id
      // this was limiting in terms of folding questions of the tree back
      // and allowing the tree to merge back to left most path
      const posOfNextQuestion =
        updatedAnswers
          .map((updatedAnswer) => {
            return updatedAnswer.q_id;
          })
          .indexOf(answer.q_id) + 1;

      const nextPotentialQuestion = updatedAnswers[posOfNextQuestion];

      if (
        next_q_id &&
        nextPotentialQuestion &&
        nextPotentialQuestion?.q_id !== next_q_id
      ) {
        // OFF LEFT MOST PATH
        const newNextQuestion = questionsSlice.filter((question) => {
          return question.q_id === next_q_id;
        })[0];

        // LEAF, remove all subsequent questions from answers
        if (newNextQuestion.node_type === "leaf") {
          // if a LEAF never delete anything (like slice)
          // instead add hidden property
          for (let i = posOfNextQuestion; i < updatedAnswers.length; i++) {
            updatedAnswers[i].node_visibility = "hidden";
          }
        } else {
          // NODE, make sure subsequent questions are prents
          // need to make left most path starting from next questions's id.
          let leftMostPathFromCurrentNode = [];
          let deeperNextQuestion = newNextQuestion;
          let deeperNextQuestionChildrenLength =
            deeperNextQuestion.q_child_nodes.length;
          const map_q_id = questionsSlice.map((question) => {
            return question.q_id;
          });

          while (deeperNextQuestionChildrenLength !== 0) {
            deeperNextQuestion =
              questions[
                map_q_id.indexOf(
                  deeperNextQuestion.q_next_step_func(
                    deeperNextQuestion,
                    [],
                    []
                  )
                )
              ];

            if (deeperNextQuestion) {
              deeperNextQuestionChildrenLength =
                deeperNextQuestion.q_child_nodes.length;

              leftMostPathFromCurrentNode.push(deeperNextQuestion);
            } else {
              deeperNextQuestionChildrenLength = 0;
            }
          }

          const earlierQuestions = updatedAnswers.slice(
            0,
            posOfNextQuestion + 2
          );

          let counter = 0;
          for (
            let i = posOfNextQuestion + 1;
            i < leftMostPathFromCurrentNode.length + posOfNextQuestion + 1;
            i++
          ) {
            if (
              updatedAnswers[i]?.q_id === leftMostPathFromCurrentNode[counter]
            ) {
              //show the node
              // updatedAnswers[i].node_type = "node";
            } else {
              //or overwrite if different
              updatedAnswers[i] = leftMostPathFromCurrentNode[counter];
              // updatedAnswers[i].node_type = "node";
            }
            counter = counter + 1;
          }

          // make sure all nodes are re-updated
          // to have node_type be node so nothing is hidden
          // if people jump around
          for (let i = 0; i < updatedAnswers.length; i++) {
            if (updatedAnswers[i].node_type === "node") {
              updatedAnswers[i].node_visibility = "visible";
            }
          }
        }

        updatedAnswers[posOfNextQuestion] = questionsSlice.filter(
          (question) => {
            return question.q_id === next_q_id;
          }
        )[0];
        updatedAnswers[posOfNextQuestion].node_visibility = "visible";
      }

      setAnswers(updatedAnswers);
    } else {
      updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0].a_error = true;

      setAnswers(updatedAnswers);
      return;
    }
  };

  const generateAnswerOptions = (answer, index, answers) => {
    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : !isNaN(currentValues[answer.q_title])
            ? currentValues[answer.q_title]
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              width: "100%",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localValue || ""}
              onChange={(e) => {
                const num = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "currency":
        const uiValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string && answer.a_string.length > 0
              ? `$${formatMoney(answer.a_string, 0)}`
              : null
            : !isNaN(currentValues[answer.q_title]) &&
              currentValues[answer.q_title]
            ? `$${formatMoney(currentValues[answer.q_title], 0)}`
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={answer.q_help_text ? answer.q_help_text : ""}
              sx={{ width: "100%" }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              inputProps={{
                inputMode: "numeric",
              }}
              value={uiValue || ""}
              onChange={(e) => {
                const num = e.target.value.replace(/\D/g, "");
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
            />
          </Box>
        );
        break;
      case "number":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
              width: "100%",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              value={
                currentValues[answer.q_title] === undefined
                  ? answer.a_string
                    ? answer.a_string
                    : null
                  : currentValues[answer.q_title]
              }
              InputLabelProps={{
                shrink:
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title],
              }}
              onChange={(e) => {
                const value = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = value;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  value,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
            />
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{ margin: theme.spacing(5, 0) }}
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localTextValue}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "address":
        // Use answer.a_string first (for example calcs), fallback to calc.Address (for saved calcs)
        const address = answer.a_string || calc?.Address || "";

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              onAddressSelect={({ place, inputValue, source }) => {
                const selectedAddress = place?.formatted_address || inputValue;
                setSuggestionChoice(selectedAddress);
                handleUserSelection(
                  selectedAddress,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              initialAddress={address !== "" ? address : ""}
              label={answer.a_error ? "Error" : answer.q_title}
              ariaLabel="Enter address of the property for this loan"
              searchOptionsTypes={[]}
              error={answer.a_error}
            />
          </Box>
        );
        break;
      case "date":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <>
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["month", "year"]}
                    label={"Date"}
                    value={
                      currentValues[answer.q_title] === undefined
                        ? null
                        : dayjs(currentValues[answer.q_title])
                    }
                    onChange={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM")
                        : "";

                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = newDateString;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        newDateString,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onBlur={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM") // it needs this input to read in // but we like to display mm / yyyy in freedomlandia
                        : "";
                      processSubmission(newDateString);
                    }}
                    format="MM/YYYY"
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Box>
            </>
          </Box>
        );
        break;
      default:
    }

    return answerOptionsUI;
  };

  const resetCalculator = () => {
    // Loop through all answers and set a_string to ""

    // Reset current values to ""
    const updatedCurrentValues = Object.keys(currentValues).reduce(
      (acc, key) => {
        acc[key] = ""; // Set each current value to an empty string
        return acc;
      },
      {}
    );

    setCurrentValues(updatedCurrentValues); // Update current values in state
  };

  const toggleAllDescriptions = () => {
    const newState = !showAllDescriptions;
    setShowAllDescriptions(newState);

    // Get all question titles from the answers, excluding "Closing cost"
    const allQuestionTitles = answers
      .map((answer) => answer.q_title)
      .filter((title) => title !== "Closing cost");

    // Create updated state with all questions set to the new state
    const updatedShowQuestionDescription = allQuestionTitles.reduce(
      (acc, title) => {
        acc[title] = newState;
        return acc;
      },
      { "Closing cost": true } // Always keep closing cost open
    );

    setShowQuestionDescription(updatedShowQuestionDescription);
  };

  return (
    <Box
      key={`calculation_${externalCalc?.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
        maxWidth: "100%",
        position: "relative",
        paddingBottom: !isInView ? "200px" : 0,
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={resetCalculator}
          sx={{
            width: "25%",
            [theme.breakpoints.down("md")]: {
              width: "33%",
            },
            "@media print": { display: "none" },
          }}
          className="no-print-element"
        >
          Reset
        </Button>
        <IconButton
          onClick={toggleAllDescriptions}
          color="info"
          sx={{
            "@media print": { display: "none" },
          }}
          className="no-print-element"
        >
          {showAllDescriptions ? <KeyboardArrowUpIcon /> : <InfoOutlinedIcon />}
        </IconButton>
      </Box>

      {answers.length > 0 ? (
        <Box id="calculator-to-print">
          <Box id="pdf-1-income">
            <QuestionRenderer
              answers={answers}
              questions={firstPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
            />
          </Box>

          <Box sx={{ margin: theme.spacing(1.5, 0) }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    flex: "0 1 50%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <ButtonBase
                    sx={{
                      width: "100%",
                      padding: theme.spacing(1),
                      "&:focus": {
                        outline: "none",
                        borderRadius: theme.spacing(0.5),
                        boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                          theme.palette.info.main
                        }`,
                      },
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1),
                        padding: 0,
                      },
                    }}
                    onClick={() => {
                      let updatedShowQuestionDescription = Object.assign(
                        {},
                        showQuestionDescription
                      );
                      updatedShowQuestionDescription[`Monthly income`] =
                        !updatedShowQuestionDescription[`Monthly income`];
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: theme.spacing(3),
                        textAlign: "left",
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(2),
                        },
                      }}
                    >
                      Monthly income
                    </Typography>
                    {showQuestionDescription[`Monthly income`] ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                      />
                    ) : (
                      <InfoOutlinedIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                        color="info"
                      />
                    )}
                  </ButtonBase>
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    fontSize: theme.spacing(2.5),
                    textAlign: "center",
                    flex: "0 1 50%",
                    margin: "auto",
                    color: theme.palette.success.main,
                  }}
                >
                  ${formatMoney(monthlyIncome, 0)}
                </Box>
              </Box>
              <Collapse in={showQuestionDescription[`Monthly income`]}>
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      margin: theme.spacing(1),
                      fontSize: theme.spacing(1.8),
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1, 0, 1, 2),
                      },
                    }}
                  >
                    ${formatMoney(parseFloat(currentValues["Salary"] || 0), 0)}{" "}
                    Salary
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Investment income"] || 0),
                      0
                    )}{" "}
                    Investment income
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Other income"] || 0),
                      0
                    )}{" "}
                    Other income
                    <br />= ${formatMoney(monthlyIncome, 0)} Total monthly
                    income
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>

          <Box id="pdf-2-expenses">
            <QuestionRenderer
              answers={answers}
              questions={secondPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
            />
          </Box>

          <Box sx={{ margin: theme.spacing(1.5, 0) }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    flex: "0 1 50%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <ButtonBase
                    sx={{
                      width: "100%",
                      padding: theme.spacing(1),
                      "&:focus": {
                        outline: "none",
                        borderRadius: theme.spacing(0.5),
                        boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                          theme.palette.info.main
                        }`,
                      },
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1),
                        padding: 0,
                      },
                    }}
                    onClick={() => {
                      let updatedShowQuestionDescription = Object.assign(
                        {},
                        showQuestionDescription
                      );
                      updatedShowQuestionDescription[`Monthly expenses`] =
                        !updatedShowQuestionDescription[`Monthly expenses`];
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: theme.spacing(3),
                        textAlign: "left",
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(2),
                        },
                      }}
                    >
                      Monthly expenses
                    </Typography>
                    {showQuestionDescription[`Monthly expenses`] ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                      />
                    ) : (
                      <InfoOutlinedIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                        color="info"
                      />
                    )}
                  </ButtonBase>
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    fontSize: theme.spacing(2.5),
                    textAlign: "center",
                    flex: "0 1 50%",
                    margin: "auto",
                    color: theme.palette.error.main,
                  }}
                >
                  ${formatMoney(monthlyExpenses, 0)}
                </Box>
              </Box>
              <Collapse in={showQuestionDescription[`Monthly expenses`]}>
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      margin: theme.spacing(1),
                      fontSize: theme.spacing(1.8),
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1, 0, 1, 2),
                      },
                    }}
                  >
                    $
                    {formatMoney(parseFloat(currentValues["Mortgage"] || 0), 0)}{" "}
                    Mortgage
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Rent"] || 0),
                      0
                    )}{" "}
                    Rent
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Property tax"] || 0),
                      0
                    )}{" "}
                    Property tax
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Insurance"] || 0),
                      0
                    )}{" "}
                    Insurance
                    <br />+ $
                    {formatMoney(parseFloat(currentValues["HOA"] || 0), 0)} HOA
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Credit card payment"] || 0),
                      0
                    )}{" "}
                    Credit card payment
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Student loan payment"] || 0),
                      0
                    )}{" "}
                    Student loan payment
                    <br />+ $
                    {formatMoney(
                      parseFloat(currentValues["Auto loan payment"] || 0),
                      0
                    )}{" "}
                    Auto loan payment
                    <br />+ $
                    {formatMoney(
                      parseFloat(
                        currentValues["Other liability payments"] || 0
                      ),
                      0
                    )}{" "}
                    Other liability payments
                    <br />= ${formatMoney(monthlyExpenses, 0)} Total monthly
                    expenses
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>

          <Box
            sx={{
              position: isInView ? "relative" : "sticky",
              minHeight: {
                xs: theme.spacing(18),
                md: "auto",
              },
              bottom: 0,
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              borderTop: !isInView
                ? `1px solid ${theme.palette.grey[300]}`
                : "none",
              transition: "all 0.3s ease",
              zIndex: 2,
            }}
          >
            <Box
              id="pdf-3-dti-calculation"
              sx={{
                padding: theme.spacing(2),
                [theme.breakpoints.down("md")]: {
                  padding: theme.spacing(2, 1),
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <ButtonBase
                      sx={{
                        width: "100%",
                        padding: theme.spacing(1),
                        "&:focus": {
                          outline: "none",
                          borderRadius: theme.spacing(0.5),
                          boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                            theme.palette.info.main
                          }`,
                        },
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          padding: 0,
                        },
                      }}
                      onClick={() => {
                        let updatedShowQuestionDescription = Object.assign(
                          {},
                          showQuestionDescription
                        );
                        updatedShowQuestionDescription[`Debt-to-Income Ratio`] =
                          !updatedShowQuestionDescription[
                            `Debt-to-Income Ratio`
                          ];
                        setShowQuestionDescription(
                          updatedShowQuestionDescription
                        );
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: theme.spacing(3),
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(2.5),
                          },
                        }}
                      >
                        Debt-to-Income Ratio
                      </Typography>
                      {showQuestionDescription[`Debt-to-Income Ratio`] ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                          color="info"
                        />
                      )}
                    </ButtonBase>
                  </Box>
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      justifyContent: "flex-end",
                      fontSize: theme.spacing(3),
                      fontWeight: 600,
                      color:
                        dtiRatio <= 43
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                    }}
                  >
                    {dtiRatio.toFixed(1)}%
                  </Box>
                </Box>
                <Collapse in={showQuestionDescription[`Debt-to-Income Ratio`]}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: theme.spacing(1),
                        fontSize: theme.spacing(1.8),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 0, 1, 2),
                        },
                      }}
                    >
                      ${formatMoney(monthlyExpenses, 0)} Monthly expenses
                      <br />/ ${formatMoney(monthlyIncome, 0)} Monthly income
                      <br />= {dtiRatio.toFixed(1)}% Debt-to-Income Ratio
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>

          {/* DTI Scale Box - Now outside sticky area */}
          <Box
            id="pdf-3-dti-standards"
            sx={{
              mt: 3,
              p: 3,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1,
              boxShadow: `0px 2px 4px ${theme.palette.grey[200]}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Standards for Debt-to-Income (DTI) ratio
            </Typography>

            {/* Scale Bar */}
            <Box sx={{ position: "relative", width: "100%", height: 20 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: 1,
                  background: `linear-gradient(to right, 
                    ${theme.palette.success.main} 0%, 
                    ${theme.palette.success.main} 35%, 
                    #FFA726 35%,
                    #FFA726 50%,
                    ${theme.palette.error.main} 50%,
                    ${theme.palette.error.main} 100%
                  )`,
                }}
              />
              {/* Indicator Triangle */}
              <Box
                sx={{
                  position: "absolute",
                  top: -10,
                  left: `${Math.min(Math.max(dtiRatio, 0), 100)}%`,
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: "10px solid black",
                }}
              />
            </Box>

            {/* Scale Labels */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1,
                px: 1,
              }}
            >
              <Box sx={{ flex: "0 0 35%", textAlign: "left" }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold", color: theme.palette.success.main }}
                >
                  35% or less: Great
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 15%", textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold", color: "#FFA726" }}
                >
                  36-49%
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 35%", textAlign: "right" }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold", color: theme.palette.error.main }}
                >
                  50%+: Needs improvement
                </Typography>
              </Box>
            </Box>

            {/* DTI Status Message */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "rgba(0, 0, 0, 0.02)",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "medium",
                  color:
                    dtiRatio <= 35
                      ? theme.palette.success.main
                      : dtiRatio <= 49
                      ? "#FFA726"
                      : theme.palette.error.main,
                }}
              >
                {dtiRatio <= 35
                  ? "35% or less: Looking Good - Relative to your income, your debt is at a manageable level. You likely have sufficient funds available for saving and spending after paying your monthly obligations. Most lenders view this DTI range very favorably."
                  : dtiRatio <= 49
                  ? "36-49%: Room for Improvement - While you're managing your current debt obligations, this DTI range may limit some lending options. Consider strategies to either increase your income or reduce monthly debt payments to improve your financial flexibility."
                  : "50% or more: Action Recommended - Having half or more of your monthly income going to debt payments significantly limits your financial options. Most lenders consider this DTI ratio too high, and you may have difficulty qualifying for new loans. Focus on debt reduction strategies."}
              </Typography>
            </Box>
          </Box>

          <Box id="pdf-4-save-share-pdf-questions">
            <SaveSharePDF
              answers={answers}
              allowedQuestionTitles={thirdPart}
              generateAnswerOptions={generateAnswerOptions}
              calc={calc}
              setCalc={setCalc}
              calculatorType={calcType}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
      <Dialog
        open={showLogInDialog}
        onClose={() => setShowLogInDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Authenticator customTitle={"Log in or sign up to do that"} />
        </DialogContent>
      </Dialog>
      <Box id="closing-cost-anchor" />
    </Box>
  );
}
